import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import { AnchorTag, FormattedText } from "@tm/static";
import { theme } from "@tm/utility/global";
import React, { useEffect, useRef, useState } from "react";
import { IEnhancedLink } from "../type";
import DropdownModal from "./DropdownModal";
import {
  DropdownArrow,
  DropdownToggle,
  LinkContainer,
  LinksContainer,
  LinkSeparator,
} from "./MoreBenefits.style";

const analytics = getAnalytics();

interface EnhancedLinksSectionProps {
  links?: IEnhancedLink[];
  ctaLink?: string | undefined;
  tncLink?: string;
  quoteId?: string; // Add quoteId for the new API endpoint
  csrfToken?: string;
}

const EnhancedLinksSection: React.FC<EnhancedLinksSectionProps> = ({
  links,
  ctaLink,
  tncLink,
  quoteId,
  csrfToken,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Backward compatibility: if links array doesn't exist but old props do, create fallback
  const effectiveLinks: IEnhancedLink[] =
    links ||
    (ctaLink && tncLink
      ? [
          {
            id: "tnc",
            title: { text: { value: ctaLink } },
            type: "link" as const,
            url: tncLink,
          },
        ]
      : []);

  if (!effectiveLinks.length) return null;

  const handleDropdownToggle = (linkId: string) => {
    if (openDropdown === null) {
      analytics?.trackListingEvents({
        actionType: `download_benefits_clicked_view_details_modal`,
      });
    }
    setOpenDropdown(openDropdown === linkId ? null : linkId);
  };

  const handleLinkClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <LinksContainer>
      {effectiveLinks.map((link) => (
        <React.Fragment key={link.id}>
          {link.type === "link" ? (
            <AnchorTag
              fontSize="xs"
              fontWeight="bold"
              color={theme.blueText}
              hrefProp={link.url}
              targetProp="_blank"
              handleClick={() => {
                analytics?.trackListingEvents(
                  { actionType: "tnc_link_clicked" as any },
                  "pageView"
                );
                trackEvent({
                  event_detail: {
                    event_type: "action",
                    event_name: "component_cta-clicked",
                    event_value: "tnc_link_clicked",
                  },
                });
              }}
            >
              <FormattedText value={link.title} />
            </AnchorTag>
          ) : (
            <LinkContainer ref={dropdownRef}>
              <DropdownToggle
                onClick={() => handleDropdownToggle(link.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleDropdownToggle(link.id);
                  }
                }}
                tabIndex={0}
              >
                <AnchorTag
                  fontSize="xs"
                  fontWeight="bold"
                  color={theme.blueText}
                >
                  <FormattedText value={link.title} />
                </AnchorTag>
                <DropdownArrow
                  isOpen={openDropdown === link.id}
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4.33 5.73L8 9.73L11.67 5.73"
                    stroke="#0084FF"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </DropdownArrow>
              </DropdownToggle>

              {openDropdown === link.id && link.dropdownOptions && (
                <DropdownModal
                  options={link.dropdownOptions}
                  quoteId={quoteId}
                  onClose={() => setOpenDropdown(null)}
                  csrfToken={csrfToken}
                />
              )}
            </LinkContainer>
          )}

          {/* Add separator line except for the last item */}
          {effectiveLinks.indexOf(link) < effectiveLinks.length - 1 && (
            <LinkSeparator />
          )}
        </React.Fragment>
      ))}
    </LinksContainer>
  );
};

export default EnhancedLinksSection;
